// @flow
import React, { Fragment } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import colors from '../../../assets/css/colors';
import isTimeOut from '../../../utils/isTimeOut';
import {
	ButtonPrimary,
	CountdownTimer,
	LeagueAvatar,
	LeagueName,
	ModalBody,
	ModalContainer,
} from '../../../components';
import { BYE_ROUNDS } from '../../../helpers/index';

const JoinLeagueSettingsPopupHeading = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	background: #fff;
	box-shadow: 0 1px 0 0 rgba(0,0,0,0.25);
	height: 70px;
	padding: 20px;
	box-sizing: border-box;

	> div {
		width: 50%;
		display: flex;
		align-items: center;
		box-sizing: border-box;
	}

	img {
		margin-right: 1em;
	}
`;

const JoinLeagueSettingsPopupOption = styled.div`
	padding: 0 40px;
	box-sizing: border-box;
	margin: 10px 0;

	h3 {
		color: #C0C0C0;
		font-size: 15px;
		font-weight: bold;
		line-height: 1.46;
	}

	> div {
		margin: .5em auto;
		box-sizing: border-box;

		> div {
			box-shadow: inset 0 -1px 0 0 ${colors.lightGrey};
			background: #fff;
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding: 0 10px;
			box-sizing: border-box;

			p:first-child {
				color: ${colors.title};
				font-size: 12px;
				line-height: 1.66;
			}
			p:last-child {
				color: ${colors.title};
				font-size: 12px;
				font-weight: bold;
				line-height: 1.66;
				text-align: right;
			}
		}
	}
`;

const JoinLeagueSettingsPopupFooter = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	margin: 20px 0 1em;

	button {
		width: 200px;
	}
`;

const CountdownWrapper = styled.div`
	justify-content: flex-end;

	.countdown-title {
		font-size: 10px;
		letter-spacing: 0.5px;
		line-height: 1.4;
		color: ${colors.nrlDarkGreen}
		margin-right: 1em
	}
`;

const onOff = param => param ? 'ON' : 'OFF';
const waiverPeriod = period => period === 1 ? '1 day' : '2 days';
const waiverOrder = order => order ? 'Last pick to bottom' : 'Reverse ladder order';

type PropsOption = {
	label: string,
	value: string | number,
	is_uppercase?: boolean
};
const PopupOption = ({ label, value, is_uppercase }: PropsOption) => (
	<div>
		<p>{label}</p>
		<p className={is_uppercase ? 'uppercase' : ''}>{value}</p>
	</div>
);

type Props = {
	league: Object,
	onClick: Function,
	join: Function,
	is_regenerated_league?: boolean
};

const getButtonText = is_regenerated_league => {
	if(is_regenerated_league) {
		return 'REGENERATE THIS LEAGUE';
	}
	return 'JOIN THIS LEAGUE';
};

type HeaderProps = {
	league: Object,
	is_regenerated_league?: boolean
}

const Header = ({ league, is_regenerated_league }:HeaderProps ) => (
	<div>
		<LeagueAvatar league={league} size="40px" />
		<LeagueName width="300px"
			name={league.name}
			lastname={league.lastname}
			firstname={league.firstname}
			commissioner={is_regenerated_league? 'You are the Commissioner' : ''}
		/>
	</div>
);

export const JoinLeagueSettingsPopup = ({
	league,
	onClick,
	join, is_regenerated_league
}: Props) => {
	if (!league) {
		return null;
	}

	return (
		<ModalContainer
			header_text="LEAGUE SETTING"
			is_sponsored={false}
			onClick={onClick}
		>

			<ModalBody>
				<JoinLeagueSettingsPopupHeading>
					<Header league={league} is_regenerated_league={is_regenerated_league} />
					{!isTimeOut(league.draft_start) ? (
						<CountdownWrapper>
							<p className="countdown-title">DRAFT STARTS IN</p>
							<CountdownTimer date={league.draft_start} is_uppercase={true} />
						</CountdownWrapper>
					) : null}
				</JoinLeagueSettingsPopupHeading>

				<JoinLeagueSettingsPopupOption>
					<h3>Basic Settings</h3>
					<div>
						<PopupOption label="League Size" value={league.size} />
						<PopupOption label="Starting Round" value={league.start_round} />
						<PopupOption label="Privacy Settings"
							value={league.privacy ? 'PUBLIC' : 'PRIVATE'}
						/>

					</div>
				</JoinLeagueSettingsPopupOption>

				<JoinLeagueSettingsPopupOption>
					<h3>Advanced Settings</h3>
					<div>
						<PopupOption label="League Finals Series"
							value={league.finals ? league.finals_format : 'OFF'}
						/>
						<PopupOption label="Play Opponents"
							value={league.play_times}
							is_uppercase={true}
						/>
						{BYE_ROUNDS.length ? (
							<PopupOption label="Bye Rounds"
								value={onOff(league.bye_rounds)}
							/>
						) : null}
					</div>
				</JoinLeagueSettingsPopupOption>

				<JoinLeagueSettingsPopupOption>
					<h3>Draft Options</h3>
					<div>
						<PopupOption label="Draft Type"
							value={league.draft_type}
							is_uppercase={true}
						/>
						<PopupOption label="Draft Order"
							value={league.draft_order}
							is_uppercase={true}
						/>
						<PopupOption label="Draft Turn Time"
							value={league.draft_turn}
						/>
						<PopupOption label="Draft Date & Time"
							value={moment(league.draft_start).format('DD/MM/YY HH:mm')}
						/>
					</div>
				</JoinLeagueSettingsPopupOption>

				<JoinLeagueSettingsPopupOption>
					<h3>Game Play</h3>
					<div>
						<PopupOption label="Squad and Size Layout"
							value={league.squad_size}
						/>
						<PopupOption label="Captains" value={onOff(league.captains)} />
						<PopupOption label="Emergency" value={onOff(league.emergency)} />
						<PopupOption label="Rolling Lockout" value={onOff(league.lockout)} />
					</div>
				</JoinLeagueSettingsPopupOption>

				<JoinLeagueSettingsPopupOption>
					<h3>Trades & Restricted Free Agents</h3>
					<div>
						<PopupOption label="Restricted Free Agents"
							value={onOff(league.trade_waivers)}
						/>

						{league.trade_waivers ? (
							<Fragment>
								<PopupOption label="RFA Time Period"
									value={waiverPeriod(league.waiver_period)} />
								<PopupOption label="RFA Order Policy"
									value={waiverOrder(league.waiver_order)} />
							</Fragment>
						) : null}
						<PopupOption label="RFA privacy settings"
							value={league.rfa_privacy}
							is_uppercase={true}
						/>
						<PopupOption label="Trade Approval Options"
							value={league.approve_trade}
							is_uppercase={true}
						/>
					</div>
				</JoinLeagueSettingsPopupOption>

				<JoinLeagueSettingsPopupFooter>
					<ButtonPrimary onClick={join.bind(this, league.id)} width="200px">
						{getButtonText(is_regenerated_league)}
					</ButtonPrimary>
				</JoinLeagueSettingsPopupFooter>
			</ModalBody>
		</ModalContainer>
	);
};

export default JoinLeagueSettingsPopup;


// WEBPACK FOOTER //
// ./src/pages/leagues/leaguesJoin/JoinLeagueSettingsPopup.js